import { type Dispatch, type SetStateAction } from 'react';
import { Item } from 'react-stately';
import { useExperiment } from '@hungryroot/experiments';
import { Truck } from '@hungryroot/icons';
import {
  Loading,
  LoadingVariant,
} from '@hungryroot/loading-component-primitives';
import { isZipcodeError } from '@hungryroot/pages-checkout';
import { Heading, Paragraph, Select } from '@hungryroot/ramen';
import {
  type DateInfo,
  type PossibleDays,
} from '../../../../api/get_possible_days/get/get_possible_days';
import styles from './FirstDeliveryDate.module.css';

interface Props {
  testid?: string;
  zipcode: string | undefined;
  orderId: number;
  setSelectedDate: Dispatch<SetStateAction<DateInfo | undefined>>;
  selectedDate: DateInfo | undefined;
  isLoading: boolean;
  error: string;
  deliveryDates: PossibleDays | undefined;
}

const getTempDate = (
  selectedDate: DateInfo | undefined,
  deliveryDates: PossibleDays | undefined
) => {
  const tempBlackoutDates = deliveryDates?.temp_blackout_dates;

  if (selectedDate === undefined || tempBlackoutDates === undefined)
    return null;

  if (!Object.keys(tempBlackoutDates).includes(selectedDate.value)) return null;

  const tempBlackoutDate = tempBlackoutDates[selectedDate.value][0];

  // feeding Intl.DateTimeFormat a date with dashes makes the output one day off
  // this is fixed by replacing dashes with slashes
  const futureOrderDOW = new Intl.DateTimeFormat('en-us', {
    weekday: 'long',
  }).format(new Date(tempBlackoutDate.replace(/-/g, '/')));

  return futureOrderDOW;
};

const currentDeliveryDate = (
  selectedDate: DateInfo | undefined,
  deliveryDates: PossibleDays | undefined
) => {
  const selectedDOW = selectedDate && selectedDate.label.split(',')[0];

  const futureOrderDOW = getTempDate(selectedDate, deliveryDates);

  let text = `Your weekly delivery day is currently ${selectedDOW}. You can always change or skip upcoming deliveries.`;

  if (futureOrderDOW && selectedDate?.label) {
    text = `Your first delivery is coming on ${selectedDate.label} because of a holiday. After that, your regular delivery day of the week will be ${futureOrderDOW}. You can always change it for upcoming deliveries.`;
  }
  return (
    <div className={styles['current-delivery-container']}>
      <Truck />
      <Paragraph className={styles['current-date']}>{text}</Paragraph>
    </div>
  );
};

export function FirstDeliveryDate(props: Props) {
  const {
    testid,
    zipcode,
    setSelectedDate,
    selectedDate,
    isLoading,
    error,
    deliveryDates,
  } = props;

  const exp159 = useExperiment(159);
  const inExp159Variant = exp159 !== undefined && exp159 > 0;

  const isZipError = zipcode && isZipcodeError(zipcode);

  const dateSelector =
    !error && deliveryDates ? (
      <>
        <Select
          testid={`${testid}-date`}
          label="Date"
          items={deliveryDates?.possible_dates}
          className={styles.item}
          selectedKey={selectedDate?.value}
          onSelectionChange={(key) => {
            let selected = selectedDate;
            deliveryDates?.possible_dates.filter((date) => {
              date.value === key ? (selected = date) : selected;
            });
            setSelectedDate(selected);
          }}
        >
          {(item) => <Item key={item.value}>{item.label}</Item>}
        </Select>
        {currentDeliveryDate(selectedDate, deliveryDates)}
      </>
    ) : (
      <Paragraph className={styles.error}>
        Something went wrong. Please refresh.
      </Paragraph>
    );

  if (exp159 === undefined) {
    return null;
  }

  return (
    <div className={styles.container} data-testid={testid}>
      <Heading
        variant={inExp159Variant ? 'H3' : 'H4'}
        className={styles.heading}
      >
        First delivery date
      </Heading>
      {zipcode && !isZipError ? (
        <div
          className={`${styles['valid-container']} ${
            isLoading && styles.loading
          }`}
        >
          {isLoading ? (
            <Loading variant={LoadingVariant.InlineButton} />
          ) : (
            dateSelector
          )}
        </div>
      ) : (
        <Paragraph className={styles.invalid}>
          Please enter your zip code to see available delivery dates.
        </Paragraph>
      )}
    </div>
  );
}
